import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import Swal from "sweetalert2";
import api from "../../../../../../utils/api";
import {
  useCompanyRetransmissionsStore,
  type CompanyRetransmission,
  type CompanyRetransmissionFormData,
  type RetransmissionScopeType,
} from "../../../../../../store/admin/companyRetransmissionsStore";
import { useIntegratorsStore } from "../../../../../../store/reseller-admin/integratorsStore";
import { getCustomSelectStyles } from "../../../../shared";
import type { ThemeClasses, Company } from "../types";
import Modal from "../../../../../../components/base/Modal";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface CompanyRetransmissionsModalProps {
  company: Company;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
}

type ViewMode = "list" | "add" | "edit";

interface DeviceOption {
  id: string;
  name?: string;
  identifier?: string;
}
interface GroupOption {
  id: string;
  name?: string;
  devices_ids?: string[];
}

const initialForm: CompanyRetransmissionFormData = {
  active: false,
  integrator_id: "",
  integrator_credentials: {},
  credentials_version: "0",
  scope: { type: "all", device_ids: [], group_id: null },
};

export function CompanyRetransmissionsModal({
  company,
  themeClasses,
  isDarkMode,
  onClose,
}: CompanyRetransmissionsModalProps) {
  const {
    retransmissions,
    loading,
    fetchRetransmissions,
    createRetransmission,
    updateRetransmission,
    deleteRetransmission,
    clearRetransmissions,
  } = useCompanyRetransmissionsStore();

  const integrators = useIntegratorsStore((s) => s.integrators);
  const fetchIntegrators = useIntegratorsStore((s) => s.fetchIntegrators);

  const { isUserDev } = useIsUserDev();

  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selected, setSelected] = useState<CompanyRetransmission | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CompanyRetransmissionFormData>({ defaultValues: initialForm });

  const active = watch("active");
  const integratorId = watch("integrator_id");
  const scopeType = watch("scope.type");
  const scopeDeviceIds = watch("scope.device_ids");
  const scopeGroupId = watch("scope.group_id");

  const selectedIntegrator = useMemo(
    () => integrators.find((i) => i.id === integratorId) || null,
    [integrators, integratorId],
  );
  const credentialSchema = selectedIntegrator?.credentials_schema || [];

  const selectStyles = getCustomSelectStyles(isDarkMode, false);

  const integratorOptions = integrators.map((i) => ({ value: i.id, label: i.name }));
  const deviceOptions = devices.map((d) => ({
    value: d.id,
    label: d.name || d.identifier || d.id,
  }));
  const groupOptions = groups.map((g) => ({ value: g.id, label: g.name || g.id }));

  useEffect(() => {
    fetchRetransmissions(company.id);
    fetchIntegrators({ limit: 200, page: 1 });
    // Cargar dispositivos y grupos de esta compañía para el scope
    (async () => {
      try {
        const { data } = await api.get("/devices/by-company", {
          params: { company_id: company.id },
        });
        setDevices(Array.isArray(data.data) ? data.data : data.data?.data || []);
      } catch (e) {
        console.error("Error loading company devices:", e);
      }
      try {
        const { data } = await api.get("/groupList", {
          headers: { "x-company-id": company.id },
          params: { limit: 500, page: 1 },
        });
        setGroups(Array.isArray(data.data) ? data.data : []);
      } catch (e) {
        console.error("Error loading company groups:", e);
      }
    })();
    return () => {
      clearRetransmissions();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  const inputClass = `w-full px-3 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  const openAdd = () => {
    setSelected(null);
    reset(initialForm);
    setViewMode("add");
  };

  const openEdit = (r: CompanyRetransmission) => {
    setSelected(r);
    reset({
      active: r.active ?? false,
      integrator_id: r.integrator_id || "",
      integrator_credentials: r.integrator_credentials || {},
      credentials_version: r.credentials_version || "0",
      scope: {
        type: (r.scope?.type as RetransmissionScopeType) || "all",
        device_ids: r.scope?.device_ids || [],
        group_id: r.scope?.group_id || null,
      },
    });
    setViewMode("edit");
  };

  const backToList = () => {
    setViewMode("list");
    setSelected(null);
    reset(initialForm);
  };

  const handleScopeTypeChange = (type: RetransmissionScopeType) => {
    setValue("scope.type", type, { shouldDirty: true });
    if (type === "all") {
      setValue("scope.device_ids", [], { shouldDirty: true });
      setValue("scope.group_id", null, { shouldDirty: true });
    } else if (type === "devices") {
      setValue("scope.group_id", null, { shouldDirty: true });
    } else if (type === "group") {
      setValue("scope.device_ids", [], { shouldDirty: true });
    }
  };

  const handleGroupChange = (groupId: string | null) => {
    setValue("scope.group_id", groupId, { shouldDirty: true });
    const group = groups.find((g) => g.id === groupId);
    setValue("scope.device_ids", group?.devices_ids || [], { shouldDirty: true });
  };

  const onSubmit = async (data: CompanyRetransmissionFormData) => {
    setIsSubmitting(true);

    // Solo conservar las credenciales del esquema del integrador seleccionado
    const creds: Record<string, string> = {};
    credentialSchema.forEach((f) => {
      creds[f.field] = data.integrator_credentials?.[f.field] || "";
    });

    const payload: CompanyRetransmissionFormData = {
      active: data.active,
      integrator_id: data.integrator_id,
      integrator_credentials: creds,
      credentials_version: data.credentials_version || "0",
      scope:
        data.scope.type === "all"
          ? { type: "all", device_ids: [], group_id: null }
          : data.scope.type === "group"
            ? {
                type: "group",
                device_ids: data.scope.device_ids || [],
                group_id: data.scope.group_id || null,
              }
            : {
                type: "devices",
                device_ids: data.scope.device_ids || [],
                group_id: null,
              },
    };

    const result =
      viewMode === "edit" && selected
        ? await updateRetransmission(company.id, selected.id, payload)
        : await createRetransmission(company.id, payload);

    setIsSubmitting(false);

    if (result.success) {
      await Swal.fire({
        title: "¡Éxito!",
        text:
          viewMode === "edit"
            ? "Retransmisión actualizada correctamente"
            : "Retransmisión creada correctamente",
        icon: "success",
        confirmButtonColor: "#16a34a",
      });
      backToList();
    } else {
      await Swal.fire({
        title: "Error",
        text:
          result.error ||
          (viewMode === "edit"
            ? "Error al actualizar la retransmisión"
            : "Error al crear la retransmisión"),
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleDelete = async (r: CompanyRetransmission) => {
    const integratorName =
      integrators.find((i) => i.id === r.integrator_id)?.name || r.integrator_id || r.id;
    const result = await Swal.fire({
      title: "¿Eliminar retransmisión?",
      text: `¿Realmente desea eliminar la retransmisión "${integratorName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    const res = await deleteRetransmission(company.id, r.id);
    if (res.success) {
      await Swal.fire({
        title: "¡Éxito!",
        text: "Retransmisión eliminada correctamente",
        icon: "success",
        confirmButtonColor: "#16a34a",
      });
    } else {
      await Swal.fire({
        title: "Error",
        text: res.error || "No se pudo eliminar la retransmisión",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const scopeLabel = (r: CompanyRetransmission) => {
    if (r.scope?.type === "all") return "Todos los dispositivos";
    if (r.scope?.type === "group")
      return `Grupo (${r.scope?.device_ids?.length || 0} disp.)`;
    return `${r.scope?.device_ids?.length || 0} dispositivo(s)`;
  };

  // ---- LIST VIEW ----
  const listContent = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          Retransmisiones de {company.name}
        </p>
        {isUserDev && (
          <button
            onClick={openAdd}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-primary-500 text-gray-900 text-sm font-medium hover:bg-primary-400 transition-colors"
          >
            <i className="ri-add-line"></i>
            Nueva Retransmisión
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
        </div>
      ) : retransmissions.length === 0 ? (
        <div className={`text-center py-12 ${themeClasses.textSecondary}`}>
          <i className="ri-share-forward-line text-5xl mb-3 block opacity-50"></i>
          <p className="text-sm">No hay retransmisiones configuradas para esta compañía</p>
        </div>
      ) : (
        <div className="space-y-2">
          {retransmissions.map((r) => {
            const integratorName =
              integrators.find((i) => i.id === r.integrator_id)?.name ||
              r.integrator_id ||
              "—";
            return (
              <div
                key={r.id}
                className={`flex items-center justify-between p-3 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${themeClasses.text}`}>
                      {integratorName}
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        r.active
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                      }`}
                    >
                      {r.active ? "Activa" : "Inactiva"}
                    </span>
                  </div>
                  <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
                    {scopeLabel(r)}
                  </p>
                </div>
                {isUserDev && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEdit(r)}
                      className="p-2 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-colors"
                      title="Editar"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(r)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ---- FORM VIEW ----
  const formContent = (
    <form id="company-retransmission-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <button
        type="button"
        onClick={backToList}
        className={`flex items-center gap-1 text-sm ${themeClasses.textSecondary}`}
      >
        <i className="ri-arrow-left-line"></i>
        Volver a la lista
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Integrator */}
        <div className="sm:col-span-2">
          <label className={labelClass}>
            Integrador <span className="text-red-500">*</span>
          </label>
          <Select
            options={integratorOptions}
            styles={selectStyles}
            placeholder="Seleccionar integrador"
            value={integratorOptions.find((o) => o.value === integratorId) || null}
            onChange={(opt: any) => setValue("integrator_id", opt?.value || "", { shouldDirty: true })}
            isSearchable
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {/* registro oculto para validación required */}
          <input
            type="hidden"
            {...register("integrator_id", { required: "El integrador es requerido" })}
          />
          {errors.integrator_id && (
            <p className="text-red-500 text-xs mt-1">{errors.integrator_id.message}</p>
          )}
        </div>

        {/* Estado */}
        <div>
          <label className={labelClass}>Estado</label>
          <button
            type="button"
            onClick={() => setValue("active", !active, { shouldDirty: true })}
            className={`w-full flex items-center justify-center gap-2 px-3 h-[42px] rounded-xl border text-sm transition-all ${
              active
                ? "bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`
            }`}
          >
            <i className={`${active ? "ri-toggle-fill" : "ri-toggle-line"} text-base`}></i>
            {active ? "Activa" : "Inactiva"}
          </button>
        </div>

        {/* Credentials version */}
        <div>
          <label className={labelClass}>Versión de credenciales</label>
          <input
            {...register("credentials_version")}
            className={`${inputClass} opacity-70 cursor-not-allowed`}
            placeholder="Ej: 0"
            readOnly
          />
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
            Se incrementa automáticamente al cambiar las credenciales.
          </p>
        </div>
      </div>

      {/* Dynamic integrator credentials */}
      <div>
        <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-2 flex items-center gap-1`}>
          <i className="ri-key-2-line"></i> Credenciales del integrador
        </h3>
        {!selectedIntegrator ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            Selecciona un integrador para cargar sus credenciales.
          </p>
        ) : credentialSchema.length === 0 ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            Este integrador no tiene esquema de credenciales.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {credentialSchema.map((f) => {
              const isVisible = !!visibleSecrets[f.field];
              return (
                <div key={f.field}>
                  <label className={labelClass}>
                    {f.label || f.field}
                    {f.secret && (
                      <i className="ri-lock-line ml-1 text-amber-500" title="Secreto"></i>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={f.secret && !isVisible ? "password" : "text"}
                      autoComplete="off"
                      {...register(`integrator_credentials.${f.field}` as const)}
                      className={`${inputClass}${f.secret ? " pr-10" : ""}`}
                      placeholder={f.field}
                    />
                    {f.secret && (
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleSecrets((prev) => ({
                            ...prev,
                            [f.field]: !prev[f.field],
                          }))
                        }
                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${themeClasses.textSecondary} ${themeClasses.buttonHover}`}
                        title={isVisible ? "Ocultar" : "Mostrar"}
                      >
                        <i className={isVisible ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Scope */}
      <div>
        <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-2 flex items-center gap-1`}>
          <i className="ri-focus-3-line"></i> Alcance (scope)
        </h3>
        <div className="flex gap-2 mb-3">
          {([
            { value: "all", label: "Todos" },
            { value: "devices", label: "Dispositivos" },
            { value: "group", label: "Grupo" },
          ] as { value: RetransmissionScopeType; label: string }[]).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleScopeTypeChange(opt.value)}
              className={`flex-1 py-2 px-3 rounded-xl border text-sm font-medium transition-all ${
                scopeType === opt.value
                  ? "bg-primary-500 border-primary-500 text-gray-900"
                  : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {scopeType === "all" && (
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            Se retransmitirán todos los dispositivos de la compañía.
          </p>
        )}

        {scopeType === "devices" && (
          <Select
            isMulti
            options={deviceOptions}
            styles={selectStyles}
            placeholder="Seleccionar dispositivos"
            value={deviceOptions.filter((o) => (scopeDeviceIds || []).includes(o.value))}
            onChange={(opts: any) =>
              setValue(
                "scope.device_ids",
                (opts || []).map((o: any) => o.value),
                { shouldDirty: true },
              )
            }
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        )}

        {scopeType === "group" && (
          <>
            <Select
              options={groupOptions}
              styles={selectStyles}
              placeholder="Seleccionar grupo"
              value={groupOptions.find((o) => o.value === scopeGroupId) || null}
              onChange={(opt: any) => handleGroupChange(opt?.value || null)}
              isSearchable
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {scopeGroupId && (
              <p className={`text-xs ${themeClasses.textSecondary} mt-2`}>
                {(scopeDeviceIds || []).length} dispositivo(s) del grupo seleccionado.
              </p>
            )}
          </>
        )}
      </div>
    </form>
  );

  return (
    <Modal
      width="w-full max-w-3xl"
      themeClasses={themeClasses as never}
      onClose={onClose}
      title={
        viewMode === "list"
          ? "Retransmisiones de la Compañía"
          : viewMode === "edit"
            ? "Editar Retransmisión"
            : "Nueva Retransmisión"
      }
      content={
        <div className={isDarkMode ? "text-white" : ""}>
          {viewMode === "list" ? listContent : formContent}
        </div>
      }
      buttonAction={
        viewMode !== "list" ? (
          <button
            type="submit"
            form="company-retransmission-form"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isSubmitting
              ? "Guardando..."
              : viewMode === "edit"
                ? "Guardar Cambios"
                : "Crear Retransmisión"}
          </button>
        ) : undefined
      }
    />
  );
}
